import {
  Component,
  Input,
  OnInit,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { IonTextarea, ModalController } from '@ionic/angular';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';

@Component({
  selector: 'app-share-via-email-modal',
  templateUrl: './share-via-email-modal.component.html',
  styleUrls: ['./share-via-email-modal.component.scss'],
})
export class ShareViaEmailModalComponent implements OnInit, AfterViewInit {
  @Input() documentName = '';
  @Input() shareAction = '';
  @Input() isMobile: boolean;

  @ViewChild('emailTextarea', { static: false }) emailTextarea: IonTextarea;
  shareForm: FormGroup;
  maxEmails = 50;

  isCollapsed = false;
  keepExpanded = false;
  isFileTypePopoverOpen: boolean;
  collapsedEmails: string[] = [];
  totalEmailsCount = 0;

  selectPopoverOptions = {
    showBackdrop: false,
    cssClass: 'download-order-popover',
    mode: 'ios',
    alignment: 'center',
  };

  constructor(
    private modalController: ModalController,
    private formBuilder: FormBuilder,
  ) {}

  ngOnInit() {
    this.shareForm = this.formBuilder.group({
      emailTo: [
        '',
        [
          Validators.required,
          this.preventOverMaxLength(500),
          this.validateEmails.bind(this),
        ],
      ],
      message: [
        '',
        [this.preventOverMaxLength(200), this.messageCharacterValidator],
      ],
      sendCopy: [false],
      includeProductPrices: [true],
      fileFormat: ['pdf'],
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.checkEmailLines();
    }, 0);
  }

  toggleCollapse() {
    this.isCollapsed = false;
    this.keepExpanded = true;

    setTimeout(() => {
      this.emailTextarea.setFocus();
    }, 0);
  }

  async checkEmailLines() {
    const textarea = await this.emailTextarea.getInputElement();

    let lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight, 10);
    if (isNaN(lineHeight)) {
      lineHeight = 16;
    }

    const lines = Math.floor(textarea.scrollHeight / lineHeight);
    const emails = this.emailTo.value
      .split(',')
      .map((email: string) => email.trim());
    this.totalEmailsCount = emails.length;

    if (lines > 2 && !this.keepExpanded) {
      this.isCollapsed = true;
      this.collapsedEmails = emails.slice(2);
      setTimeout(() => {
        this.emailTextarea.setFocus();
      }, 0);
    } else {
      this.isCollapsed = false;
    }
  }

  dismissModal() {
    this.modalController.dismiss();
  }

  async shareDocument() {
    if (this.shareForm.valid) {
      const emailData = this.shareForm.value;
      const emails = emailData.emailTo.split(',').map(email => email.trim());
      const emailString = this.generateEmailString(emails);
      this.modalController.dismiss({
        emailData: emailData,
        emailString: emailString,
      });
    }
  }

  onEmailKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();

      const textarea = event.target as HTMLTextAreaElement;
      const cursorPosition = textarea.selectionStart;
      const value = textarea.value;
      textarea.value =
        value.slice(0, cursorPosition) + value.slice(cursorPosition);

      textarea.selectionStart = textarea.selectionEnd = cursorPosition + 1;
    }
  }

  private generateEmailString(emails: string[]): string {
    const numEmails = emails.length;
    if (numEmails <= 2) {
      return emails.join(', ');
    }
    return `${emails[0]}, ${emails[1]}, and ${numEmails - 2} others`;
  }

  private validateEmails(control: AbstractControl) {
    const value = control.value || '';
    const emails = value.split(',').map(email => email.trim());
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (emails.length > this.maxEmails) {
      return { maxEmailsExceeded: true };
    }

    for (let i = 0; i < emails.length; i++) {
      const email = emails[i];

      if (i === emails.length - 1 && value.endsWith(',')) {
        continue;
      }

      if (email && !emailPattern.test(email)) {
        return { invalidEmails: true };
      }
    }

    return null;
  }

  private messageCharacterValidator(control: AbstractControl) {
    const value = control.value || '';
    const alphanumericAndSpecialPattern =
      /^[\w.,!@#$%^&*()_+[\]{}:;"'<>,.?\/\\|`~\-= ]*$/;

    if (!alphanumericAndSpecialPattern.test(value)) {
      return { invalidChars: true };
    }

    return null;
  }

  private preventOverMaxLength(maxLength: number) {
    return (control: AbstractControl) => {
      const value = control.value || '';

      if (value && value.length > maxLength) {
        control.setValue(value.substring(0, maxLength), { emitEvent: false });
        return { maxLengthExceeded: true };
      }

      return null;
    };
  }

  get emailTo() {
    return this.shareForm.get('emailTo');
  }

  get message() {
    return this.shareForm.get('message');
  }
}
